import { Component, Input, OnInit, inject, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import {
  Advertiser,
  CampaignsOutboundEventConfigurationApiClient,
  OutboundEventIntegrationConfigDto,
  UpdateOutboundEventConfigRequestDto,
  OutboundEventProvider,
  OutboundEvents
} from 'src/app/api/api-campaigns.service.generated';

@Component({
  selector: 'app-advertiser-outbound-events',
  standalone: true,
  templateUrl: './advertiser-outbound-events.component.html',
  styleUrls: ['./advertiser-outbound-events.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatSnackBarModule
  ]
})
export class AdvertiserOutboundEventsComponent implements OnInit, OnChanges {
  @Input() advertiser: Advertiser;
  @ViewChild('eventTypeInput') eventTypeInput: ElementRef<HTMLInputElement>;

  configForm: FormGroup;
  eventTypeControl = new FormControl('');
  isLoading = false;
  isSaving = false;
  availableEventTypes: OutboundEvents[] = [];
  selectedEventTypes: OutboundEvents[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  providers = Object.values(OutboundEventProvider);
  
  // Lưu tất cả configs
  allConfigs: OutboundEventIntegrationConfigDto[] = [];
  // Config hiện tại đang xử lý (Segment)
  currentConfig: OutboundEventIntegrationConfigDto | null = null;

  get isProviderNone(): boolean {
    return this.configForm?.get('provider')?.value === OutboundEventProvider.None;
  }

  get isProviderSegment(): boolean {
    return this.configForm?.get('provider')?.value === OutboundEventProvider.Segment;
  }

  get isProviderWebhook(): boolean {
    return this.configForm?.get('provider')?.value === OutboundEventProvider.Webhook;
  }

  isCheckingConnection = false;

  private fb = inject(FormBuilder);
  private apiClient = inject(CampaignsOutboundEventConfigurationApiClient);
  private snackBar = inject(MatSnackBar);
  private http = inject(HttpClient);

  ngOnInit() {
    this.initializeForm();
    this.loadAvailableEventTypes();
    if (this.advertiser?.id) {
      this.loadConfiguration();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['advertiser'] && !changes['advertiser'].firstChange && this.advertiser?.id) {
      this.loadConfiguration();
    }
  }

  private initializeForm() {
    this.configForm = this.fb.group({
      provider: [OutboundEventProvider.Segment, Validators.required],
      writeKey: [''],
      webhookEndpoint: [''],
      webhookSecurityKey: ['']
    });

    // Watch provider changes to update validators and disable fields
    this.configForm.get('provider')?.valueChanges.subscribe(provider => {
      const writeKeyControl = this.configForm.get('writeKey');
      const webhookEndpointControl = this.configForm.get('webhookEndpoint');
      const webhookSecurityKeyControl = this.configForm.get('webhookSecurityKey');

      // Clear all provider-specific validators first
      writeKeyControl?.clearValidators();
      writeKeyControl?.setValue('');
      webhookEndpointControl?.clearValidators();
      webhookEndpointControl?.setValue('');
      webhookSecurityKeyControl?.clearValidators();
      webhookSecurityKeyControl?.setValue('');

      if (provider === OutboundEventProvider.None) {
        this.selectedEventTypes = [];
      } else if (provider === OutboundEventProvider.Segment) {
        writeKeyControl?.setValidators([Validators.required]);
      } else if (provider === OutboundEventProvider.Webhook) {
        webhookEndpointControl?.setValidators([Validators.required]);
        webhookSecurityKeyControl?.setValidators([Validators.required]);
      }

      writeKeyControl?.updateValueAndValidity();
      webhookEndpointControl?.updateValueAndValidity();
      webhookSecurityKeyControl?.updateValueAndValidity();
    });
  }

  private loadAvailableEventTypes() {
    this.apiClient.getEventTypes().subscribe({
      next: (eventTypes) => {
        this.availableEventTypes = eventTypes as OutboundEvents[];
      },
      error: (error) => {
        console.error('Error loading event types:', error);
        this.snackBar.open('Failed to load available event types', 'Close', { duration: 3000 });
      }
    });
  }

  private loadConfiguration() {
    if (!this.advertiser?.id) return;

    this.isLoading = true;
    this.apiClient.getAdvertiserConfig(this.advertiser.id).subscribe({
      next: (configs) => {
        // Lưu tất cả configs
        this.allConfigs = configs || [];
        
        this.currentConfig = this.allConfigs.find(c => c.provider === OutboundEventProvider.Segment)
          || this.allConfigs.find(c => c.provider === OutboundEventProvider.Webhook)
          || this.allConfigs[0] || null;
        
        if (this.currentConfig) {
          this.populateForm(this.currentConfig);
        } else {
          // Initialize with default empty config
          this.populateForm({
            provider: OutboundEventProvider.Segment,
            credentials: {},
            subscribedEventTypes: []
          } as OutboundEventIntegrationConfigDto);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading configuration:', error);
        this.isLoading = false;
        this.allConfigs = [];
        this.currentConfig = null;
        // Initialize with default empty config
        this.populateForm({
          provider: OutboundEventProvider.Segment,
          credentials: {},
          subscribedEventTypes: []
        } as OutboundEventIntegrationConfigDto);
      }
    });
  }

  private populateForm(config: OutboundEventIntegrationConfigDto) {
    this.configForm.patchValue({
      provider: config.provider || OutboundEventProvider.Segment,
      writeKey: config.credentials?.['WriteKey'] || '',
      webhookEndpoint: config.credentials?.['WebhookEndpoint'] || '',
      webhookSecurityKey: config.credentials?.['SecurityKey'] || ''
    });

    this.selectedEventTypes = config.subscribedEventTypes || [];
  }

  addEventType(event: MatChipInputEvent): void {
    const value = (event.value || '').trim() as OutboundEvents;

    if (value && this.availableEventTypes.includes(value) && !this.selectedEventTypes.includes(value)) {
      this.selectedEventTypes.push(value);
    }

    event.chipInput!.clear();
  }

  removeEventType(eventType: OutboundEvents): void {
    const index = this.selectedEventTypes.indexOf(eventType);
    if (index >= 0) {
      this.selectedEventTypes.splice(index, 1);
    }
  }

  onEventTypeSelected(eventType: OutboundEvents): void {
    if (eventType && !this.selectedEventTypes.includes(eventType)) {
      this.selectedEventTypes.push(eventType);
    }
    this.eventTypeControl.setValue('');
    if (this.eventTypeInput) {
      this.eventTypeInput.nativeElement.value = '';
    }
  }

  addEventTypeFromList(eventType: OutboundEvents): void {
    if (!this.selectedEventTypes.includes(eventType)) {
      this.selectedEventTypes.push(eventType);
      this.configForm.markAsDirty();
    }
  }

  onSave() {
    if (this.configForm.invalid) {
      this.snackBar.open('Please fill in all required fields', 'Close', { duration: 3000 });
      return;
    }

    const formValue = this.configForm.value;
    let credentials: { [key: string]: string } = {};
    if (formValue.provider === OutboundEventProvider.Segment) {
      credentials = { 'WriteKey': formValue.writeKey };
    } else if (formValue.provider === OutboundEventProvider.Webhook) {
      credentials = {
        'WebhookEndpoint': formValue.webhookEndpoint,
        'SecurityKey': formValue.webhookSecurityKey
      };
    }

    const request = new UpdateOutboundEventConfigRequestDto({
      provider: formValue.provider,
      credentials: credentials,
      subscribedEventTypes: formValue.provider === OutboundEventProvider.None ? [] : this.selectedEventTypes
    });

    this.isSaving = true;
    this.apiClient.updateAdvertiserConfig(this.advertiser.id, request).subscribe({
      next: (config) => {
        this.isSaving = false;
        this.snackBar.open('Outbound event configuration saved successfully', 'Close', { duration: 3000 });
        
        this.currentConfig = config;
        const existingIndex = this.allConfigs.findIndex(c => c.provider === config.provider);
        if (existingIndex >= 0) {
          this.allConfigs[existingIndex] = config;
        } else {
          this.allConfigs.push(config);
        }
        
        this.populateForm(config);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error saving configuration:', error);
        this.snackBar.open('Failed to save configuration: ' + (error.response || error.message), 'Close', { duration: 5000 });
      }
    });
  }

  checkConnection() {
    const endpoint = this.configForm.get('webhookEndpoint')?.value;
    const securityKey = this.configForm.get('webhookSecurityKey')?.value;

    if (!endpoint || !securityKey) {
      this.snackBar.open('Webhook endpoint and security key are required', 'Close', { duration: 3000 });
      return;
    }

    const payload = {
      provider: 'Webhook',
      eventType: 'CardReceived',
      data: {
        timestamp: new Date().toISOString()
      }
    };

    const headers = new HttpHeaders({ 'Security-Key': securityKey });

    this.isCheckingConnection = true;
    this.http.post(endpoint, payload, { headers }).subscribe({
      next: () => {
        this.isCheckingConnection = false;
        this.snackBar.open('Connection successful!', 'Close', { duration: 3000 });
      },
      error: (error) => {
        this.isCheckingConnection = false;
        this.snackBar.open('Connection failed: ' + (error.message || error.statusText), 'Close', { duration: 5000 });
      }
    });
  }

  onCancel() {
    this.loadConfiguration();
  }

  get isFormDirty(): boolean {
    return this.configForm.dirty || this.hasEventTypesChanged();
  }

  private hasEventTypesChanged(): boolean {
    // Compare with original loaded configuration
    // This is a simplified check - you might want to store original config for proper comparison
    return false;
  }
}
